import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './booking.css';
import Layout from '../components/layout';
import { supabase } from '../supabase';
import { gsap, prefersReducedMotion, useGsapAnimations } from '../hooks/useGsapAnimations';

const Booking = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [isBookingOpen, setIsBookingOpen] = useState(true);
  const [bookingTimeMsg, setBookingTimeMsg] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef(null);
  const didSwipe = useRef(false);
  const pageRef = useRef(null);
  const popupRef = useRef(null);

  useGsapAnimations(pageRef, tickets.length);

  useEffect(() => {
    const getTickets = async () => {
      const { data } = await supabase
        .from('tickets')
        .select('*')
        .order('id', { ascending: true });

      setTickets(data);
      setSelectedTicket(data[0]);
    };

    const getEventSettings = async () => {
      const { data } = await supabase
        .from('event_settings')
        .select('booking_start_date, booking_end_date')
        .single();
      
      if (data) {
        const now = new Date();
        const start = data.booking_start_date ? new Date(data.booking_start_date) : null;
        const end = data.booking_end_date ? new Date(data.booking_end_date) : null;
        
        let isOpen = true;
        let msg = '';
        
        if (start && now < start) {
          isOpen = false;
          msg = 'Bookings have not started yet.';
        } else if (end && now > end) {
          isOpen = false;
          msg = 'Bookings are currently closed.';
        }
        
        setIsBookingOpen(isOpen);
        setBookingTimeMsg(msg);
      }
    };

    getTickets();
    getEventSettings();
  }, []);

  useLayoutEffect(() => {
    if (!showPopup || !popupRef.current || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      gsap.from('.booking_popup_overlay', {
        autoAlpha: 0,
        duration: 0.18,
        ease: 'power2.out',
      });

      gsap.from('.booking_popup', {
        autoAlpha: 0,
        y: 34,
        scale: 0.92,
        duration: 0.48,
        ease: 'back.out(1.55)',
      });
    }, popupRef);

    return () => ctx.revert();
  }, [showPopup]);

  const total = selectedTicket ? selectedTicket.price * quantity : 0;
  const orderedTickets = selectedTicket
    ? [selectedTicket, ...tickets.filter((ticket) => ticket.id !== selectedTicket.id)]
    : tickets;

  const selectRelativeTicket = (step) => {
    if (!tickets.length || !selectedTicket) return;

    const currentIndex = tickets.findIndex((ticket) => ticket.id === selectedTicket.id);
    const nextIndex = (currentIndex + step + tickets.length) % tickets.length;
    setSelectedTicket(tickets[nextIndex]);
  };

  const handlePointerDown = (event, ticket) => {
    if (ticket.id !== selectedTicket?.id) return;

    dragStart.current = {
      x: event.clientX,
      y: event.clientY,
    };
    didSwipe.current = false;
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleCardKeyDown = (event, ticket) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    setSelectedTicket(ticket);
  };

  const handlePointerMove = (event) => {
    if (!dragStart.current) return;

    const nextOffset = {
      x: event.clientX - dragStart.current.x,
      y: event.clientY - dragStart.current.y,
    };

    if (Math.abs(nextOffset.x) > 8 || Math.abs(nextOffset.y) > 8) {
      didSwipe.current = true;
    }

    setDragOffset(nextOffset);
  };

  const handlePointerEnd = () => {
    if (!dragStart.current) return;

    const horizontalSwipe = Math.abs(dragOffset.x) > 70;
    const verticalSwipe = Math.abs(dragOffset.y) > 90;

    if (horizontalSwipe || verticalSwipe) {
      const dominantOffset =
        Math.abs(dragOffset.x) >= Math.abs(dragOffset.y) ? dragOffset.x : dragOffset.y;

      selectRelativeTicket(dominantOffset < 0 ? 1 : -1);
    }

    dragStart.current = null;
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
  };

  const getCardStyle = (ticket) => {
    if (ticket.id !== selectedTicket?.id) return undefined;
    if (!isDragging && dragOffset.x === 0 && dragOffset.y === 0) return undefined;

    const rotation = Math.max(Math.min(dragOffset.x / 18, 8), -8);

    return {
      transform: `translate(calc(-50% + ${dragOffset.x}px), ${dragOffset.y}px) rotate(${rotation}deg) scale(1)`,
      transition: isDragging ? 'none' : undefined,
    };
  };

  const confirmBooking = async (event) => {
    event.preventDefault();
    setBookingError('');

    if (!selectedTicket) return;
    if (!fullName.trim() || !email.trim()) {
      setBookingError('Enter your full name and email to create a booking.');
      return;
    }

    const { error } = await supabase
      .from('bookings')
      .insert([
        {
          full_name: fullName,
          email: email,
          ticket_id: selectedTicket.id,
          quantity: quantity,
        }
      ]);

    if (error) {
      setBookingError(error.message);
      return;
    }

    setShowPopup(false);
    setFullName('');
    setEmail('');
  };

  return (
    <Layout>
      <div className="booking_page" ref={pageRef}>

        <section className="mb_stack" data-gsap-stagger=".mb_card" data-gsap-start="top 92%" data-gsap-from="end">
          {orderedTickets.map((ticket, index) => (
            <article
              aria-label={`${ticket.type} ticket, ${ticket.price} dollars`}
              aria-pressed={selectedTicket?.id === ticket.id}
              className={`mb_card mb_card_offset_${Math.min(index, 2)} ${selectedTicket?.id === ticket.id ? 'active_ticket' : ''}`}
              key={ticket.id}
              role="button"
              tabIndex={0}
              style={getCardStyle(ticket)}
              onPointerDown={(event) => handlePointerDown(event, ticket)}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
              onKeyDown={(event) => handleCardKeyDown(event, ticket)}
              onClick={() => {
                if (didSwipe.current) return;
                setSelectedTicket(ticket);
              }}
            >
              <div className="mb_card_content">
                <p className="mb_access_label">Access Level</p>
                <h2 className="mb_card_type">{ticket.type}</h2>

                <div className="mb_perks">
                  {ticket.features.map((feature, index) => (
                    <p className="mb_perk" key={index}>
                      <span></span>
                      {feature}
                    </p>
                  ))}
                </div>

                <strong className="mb_card_price">${ticket.price}</strong>
              </div>
            </article>
          ))}
        </section>

        <form
          className="mb_sheet"
          data-gsap-reveal
          data-gsap-direction="up"
          data-gsap-scale="0.97"
          onSubmit={(event) => {
            event.preventDefault();
            setShowPopup(true);
          }}
        >
          <div className="mb_row">
            <span className="mb_label">Quantity</span>

            <div className="mb_qty">
              <button
                type="button"
                className="mb_qty_btn"
                aria-label="Decrease ticket quantity"
                onClick={() => setQuantity((currentQuantity) => Math.max(1, currentQuantity - 1))}
              >
                -
              </button>

              <span className="mb_qty_num">{quantity}</span>

              <button
                type="button"
                className="mb_qty_btn mb_qty_plus"
                aria-label="Increase ticket quantity"
                onClick={() => setQuantity((currentQuantity) => currentQuantity + 1)}
              >
                +
              </button>
            </div>
          </div>

          <div className="mb_row mb_total_row">
            <span className="mb_label">Total Amount</span>
            <span className="mb_total">${total}</span>
          </div>

          {!isBookingOpen && (
            <p style={{ color: '#ff3333', textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem', fontWeight: '500' }}>
              {bookingTimeMsg}
            </p>
          )}

          <button className="mb_confirm_btn" type="submit" disabled={!selectedTicket || !isBookingOpen}>
            Confirm Booking
          </button>
        </form>

        {showPopup && (
          <div className="booking_popup_overlay" ref={popupRef} role="dialog" aria-modal="true" aria-labelledby="booking-popup-title">
            <form className="booking_popup" onSubmit={confirmBooking} noValidate>
              <h2 id="booking-popup-title">Write full name and email</h2>

              <label className="sr_only" htmlFor="booking-full-name">Full name</label>
              <input
                id="booking-full-name"
                type="text"
                placeholder="Full name"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                autoComplete="name"
                aria-describedby={bookingError ? 'booking-error' : undefined}
                aria-invalid={bookingError ? 'true' : undefined}
                required
              />

              <label className="sr_only" htmlFor="booking-email">Email</label>
              <input
                id="booking-email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                aria-describedby={bookingError ? 'booking-error' : undefined}
                aria-invalid={bookingError ? 'true' : undefined}
                required
              />

              {bookingError && (
                <p className="booking_popup_error" id="booking-error" role="alert">
                  {bookingError}
                </p>
              )}

              <div className="booking_popup_actions">
                <button type="button" onClick={() => setShowPopup(false)}>
                  Cancel
                </button>

                <button type="submit">
                  Create Booking
                </button>
              </div>
            </form>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default Booking;
